import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  TrendingUp,
  TrendingDown,
  Analytics,
  Refresh
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Area,
  AreaChart
} from 'recharts';
import { stocksService } from '../services/stocksService';

interface StockPrice {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface TechnicalIndicators {
  rsi: number;
  sma50: number;
  sma200: number;
  ema50: number;
  ema200: number;
  support: number;
  resistance: number;
}

interface TechnicalAnalysis {
  symbol: string;
  analyzed_at: string;
  price_statistics: {
    current_price: number;
    lifetime_high: number;
    lifetime_low: number;
    one_year_return: number;
    month_return: number;
    week_return: number;
  };
  crossover_analysis: {
    last_golden_crossover?: {
      date: string;
      price: number;
      change_since: number;
    };
    last_death_crossover?: {
      date: string;
      price: number;
      change_since: number;
    };
  };
  current_indicators: TechnicalIndicators;
}

const Charts: React.FC = () => {
  const [selectedStock, setSelectedStock] = useState<string>('RELIANCE.NS');
  const [stockPrices, setStockPrices] = useState<StockPrice[]>([]);
  const [technicalAnalysis, setTechnicalAnalysis] = useState<TechnicalAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState<string>('1Y');
  const [tabValue, setTabValue] = useState(0);
  const [availableStocks, setAvailableStocks] = useState<string[]>([]);

  useEffect(() => {
    loadAvailableStocks();
  }, []);

  useEffect(() => {
    if (selectedStock) {
      loadStockData();
      loadTechnicalAnalysis();
    }
  }, [selectedStock, timeframe]);

  const loadAvailableStocks = async () => {
    try {
      const response = await stocksService.getStockList();
      setAvailableStocks(response.symbols.slice(0, 100));
    } catch (error) {
      console.error('Error loading stock list:', error);
    }
  };

  const loadStockData = async () => {
    if (!selectedStock) return;

    setLoading(true);
    setError(null);

    try {
      const endDate = new Date();
      const startDate = new Date();
      
      switch (timeframe) {
        case '1W':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case '1M':
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case '3M':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case '6M':
          startDate.setMonth(endDate.getMonth() - 6);
          break;
        case '1Y':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        case '2Y':
          startDate.setFullYear(endDate.getFullYear() - 2);
          break;
        default:
          startDate.setFullYear(endDate.getFullYear() - 1);
      }

      const response = await stocksService.getStockPrices(
        selectedStock,
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0]
      );

      setStockPrices(response.prices);
    } catch (error: any) {
      setError(error.message || 'Failed to load stock data');
    } finally {
      setLoading(false);
    }
  };

  const loadTechnicalAnalysis = async () => {
    if (!selectedStock) return;

    try {
      const response = await stocksService.getTechnicalAnalysis(selectedStock);
      setTechnicalAnalysis(response.analysis);
    } catch (error) {
      console.error('Error loading technical analysis:', error);
    }
  };

  const handleUpdatePrices = async () => {
    try {
      setLoading(true);
      await stocksService.updateStockPrices();
      alert('Stock price update started in background');
      setTimeout(() => {
        loadStockData();
      }, 5000);
    } catch (error) {
      console.error('Error updating prices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartTechnicalAnalysis = async () => {
    try {
      setLoading(true);
      await stocksService.startTechnicalAnalysis();
      alert('Technical analysis started in background');
      setTimeout(() => {
        loadTechnicalAnalysis();
      }, 10000);
    } catch (error) {
      console.error('Error starting technical analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const chartData = stockPrices.map((price) => ({
    date: new Date(price.date).toLocaleDateString(),
    price: price.close,
    open: price.open,
    high: price.high,
    low: price.low,
    volume: price.volume,
    sma50: technicalAnalysis?.current_indicators.sma50,
    sma200: technicalAnalysis?.current_indicators.sma200,
  }));

  const currentPrice = stockPrices.length > 0 ? stockPrices[stockPrices.length - 1].close : 0;
  const priceChange = stockPrices.length > 1 ? 
    currentPrice - stockPrices[stockPrices.length - 2].close : 0;
  const priceChangePercent = stockPrices.length > 1 ? 
    (priceChange / stockPrices[stockPrices.length - 2].close) * 100 : 0;

  return (
    <Box px={1} py={0.5}>
      <Typography variant="h4" gutterBottom>
        Stock Charts & Technical Analysis
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Select Stock</InputLabel>
                <Select
                  value={selectedStock}
                  onChange={(e) => setSelectedStock(e.target.value)}
                  label="Select Stock"
                >
                  {availableStocks.map((symbol) => (
                    <MenuItem key={symbol} value={symbol}>
                      {symbol}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Timeframe</InputLabel>
                <Select
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value)}
                  label="Timeframe"
                >
                  <MenuItem value="1W">1 Week</MenuItem>
                  <MenuItem value="1M">1 Month</MenuItem>
                  <MenuItem value="3M">3 Months</MenuItem>
                  <MenuItem value="6M">6 Months</MenuItem>
                  <MenuItem value="1Y">1 Year</MenuItem>
                  <MenuItem value="2Y">2 Years</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                onClick={handleUpdatePrices}
                startIcon={<Refresh />}
                disabled={loading}
                fullWidth
              >
                Update Prices
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                onClick={handleStartTechnicalAnalysis}
                startIcon={<Analytics />}
                disabled={loading}
                fullWidth
              >
                Run Analysis
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {stockPrices.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={3}>
                <Typography variant="h6">{selectedStock}</Typography>
                <Typography variant="h4" color="primary">
                  {formatCurrency(currentPrice)}
                </Typography>
                <Box display="flex" alignItems="center" mt={1}>
                  {priceChange >= 0 ? (
                    <TrendingUp color="success" />
                  ) : (
                    <TrendingDown color="error" />
                  )}
                  <Typography
                    variant="body1"
                    color={priceChange >= 0 ? 'success.main' : 'error.main'}
                    sx={{ ml: 1 }}
                  >
                    {formatCurrency(priceChange)} ({formatPercentage(priceChangePercent)})
                  </Typography>
                </Box>
              </Grid>
              
              {technicalAnalysis && (
                <>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="textSecondary">
                      52W High / Low
                    </Typography>
                    <Typography>
                      {formatCurrency(technicalAnalysis.price_statistics.lifetime_high)} / {formatCurrency(technicalAnalysis.price_statistics.lifetime_low)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Typography variant="subtitle2" color="textSecondary">
                      1Y Return
                    </Typography>
                    <Typography color={technicalAnalysis.price_statistics.one_year_return >= 0 ? 'success.main' : 'error.main'}>
                      {formatPercentage(technicalAnalysis.price_statistics.one_year_return)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Typography variant="subtitle2" color="textSecondary">
                      RSI
                    </Typography>
                    <Typography>
                      {technicalAnalysis.current_indicators.rsi?.toFixed(2) || 'N/A'}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Price Chart" />
          <Tab label="Technical Indicators" />
          <Tab label="Analysis Summary" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Price Chart
            </Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={500}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="price"
                    stroke="#2196f3"
                    fill="rgba(33, 150, 243, 0.1)"
                    name="Price"
                  />
                  <Line
                    type="monotone"
                    dataKey="sma50"
                    stroke="#ff9800"
                    strokeWidth={2}
                    dot={false}
                    name="SMA 50"
                  />
                  <Line
                    type="monotone"
                    dataKey="sma200"
                    stroke="#f44336"
                    strokeWidth={2}
                    dot={false}
                    name="SMA 200"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <Typography>No data available</Typography>
            )}
          </CardContent>
        </Card>
      )}

      {tabValue === 1 && technicalAnalysis && (
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Moving Averages
                </Typography>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell>SMA 50</TableCell>
                      <TableCell>{formatCurrency(technicalAnalysis.current_indicators.sma50 || 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>SMA 200</TableCell>
                      <TableCell>{formatCurrency(technicalAnalysis.current_indicators.sma200 || 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>EMA 50</TableCell>
                      <TableCell>{formatCurrency(technicalAnalysis.current_indicators.ema50 || 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>EMA 200</TableCell>
                      <TableCell>{formatCurrency(technicalAnalysis.current_indicators.ema200 || 0)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Technical Indicators
                </Typography>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell>RSI</TableCell>
                      <TableCell>
                        <Chip
                          label={technicalAnalysis.current_indicators.rsi?.toFixed(2) || 'N/A'}
                          color={
                            (technicalAnalysis.current_indicators.rsi || 0) > 70 ? 'error' :
                            (technicalAnalysis.current_indicators.rsi || 0) < 30 ? 'success' : 'default'
                          }
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Support</TableCell>
                      <TableCell>{formatCurrency(technicalAnalysis.current_indicators.support || 0)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Resistance</TableCell>
                      <TableCell>{formatCurrency(technicalAnalysis.current_indicators.resistance || 0)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tabValue === 2 && technicalAnalysis && (
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Price Statistics
                </Typography>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell>Current Price</TableCell>
                      <TableCell>{formatCurrency(technicalAnalysis.price_statistics.current_price)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Lifetime High</TableCell>
                      <TableCell>{formatCurrency(technicalAnalysis.price_statistics.lifetime_high)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Lifetime Low</TableCell>
                      <TableCell>{formatCurrency(technicalAnalysis.price_statistics.lifetime_low)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>1 Year Return</TableCell>
                      <TableCell>
                        <Typography color={technicalAnalysis.price_statistics.one_year_return >= 0 ? 'success.main' : 'error.main'}>
                          {formatPercentage(technicalAnalysis.price_statistics.one_year_return)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Crossover Analysis
                </Typography>
                {technicalAnalysis.crossover_analysis.last_golden_crossover && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Last Golden Crossover</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>{technicalAnalysis.crossover_analysis.last_golden_crossover.date}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Price</TableCell>
                            <TableCell>{formatCurrency(technicalAnalysis.crossover_analysis.last_golden_crossover.price)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Change Since</TableCell>
                            <TableCell>
                              <Typography color={technicalAnalysis.crossover_analysis.last_golden_crossover.change_since >= 0 ? 'success.main' : 'error.main'}>
                                {formatPercentage(technicalAnalysis.crossover_analysis.last_golden_crossover.change_since)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Charts; 